import { ReactElement, useCallback, useMemo } from 'react'
import { Vector3Tuple } from 'three'
import { useTheme } from '@mui/material'
import { useElementIdToAssembly } from '@editor/hooks'
import { LineShapesMesh } from '@modugen/ui-toolkit'
import { OpacityOption } from '@modugen/ui-toolkit/dist/scene/types/opacity'
import { ThreeEvent } from '@react-three/fiber'
import { calculateStructuralAssemblyThickness } from 'src/components/manager/assemblies/utils'
import { PlanWallColors } from '../../planColoring'

interface LineShapesRenderMode {
  bodyGeometry: boolean
  outline: boolean
}

interface Props {
  walls: ShapeObject[]
  guidToHidden?: Record<string, boolean>
  /** Per-storey z translation (same map the FloorplanMesh uses) so footprints follow shifted walls. */
  storeyToElementTypeAndZTranslation?: Record<string, Partial<Record<ElementTypes, number>>>
  selectedElements?: string[]
  /** Same map the FloorplanMesh uses: wall types mapped to `false` ignore clicks and context menus. */
  typeToSelectable?: Partial<Record<ElementTypes, boolean>>
  onClickElement?: (guid: string, event: ThreeEvent<MouseEvent>) => void
  onContextMenuElement?: (guid: string, event: ThreeEvent<MouseEvent>) => void
  /** Draws the footprints in the plan export's colors (plan coloring modes) instead of the theme's. */
  planColors?: PlanWallColors | null
  /**
   * Draw the full wall footprints without their openings, like the stiffening plan PDF
   * (`make_wall_footprint_stiffening_plan`).
   */
  hideOpenings?: boolean
}

// Flat footprint: the box only needs enough vertical extent to be visible from above.
const WALL_BODY_HEIGHT = 0.01
// Draw slightly below the floorplan element lines (beams, openings of other meshes etc.).
const Z_OFFSET = -0.02
// Openings sit slightly above their wall footprint so their white fill wins the depth test.
const OPENING_Z_LIFT = 0.005
// Walls without an assigned assembly still need a footprint since their centerline is hidden.
const FALLBACK_WALL_THICKNESS = 0.2

const OPENING_COLOR = '#ffffff'

const OPACITY_OPTION_VALUES: Record<OpacityOption, number> = {
  notTransparent: 1,
  transparent: 0.4,
}

/**
 * Renders the given floorplan walls as flat footprints with their real thickness,
 * derived from the assembly assigned to each wall (falling back to a default
 * thickness when no assembly is assigned). Openings are drawn as white-filled
 * footprints on top of their wall. Replaces the FloorplanMesh wall centerlines,
 * so clicks and context menus are forwarded to the wall handlers.
 */
const RealThicknessWallsMesh = ({
  walls,
  guidToHidden,
  storeyToElementTypeAndZTranslation,
  selectedElements,
  typeToSelectable,
  onClickElement,
  onContextMenuElement,
  planColors,
  hideOpenings = false,
}: Props): ReactElement | null => {
  const { scenePalette } = useTheme()

  const elementIdToAssembly = useElementIdToAssembly()

  const {
    elements,
    openingGuidToWallGuid,
    wallGuidToElementType,
    guidToAdditionalTranslation,
    guidToHidden: guidToHiddenMerged,
  } = useMemo(() => {
    const elements: {
      id: string
      start: Vector3Tuple
      end: Vector3Tuple
      width: number
      height: number
    }[] = []
    const openingGuidToWallGuid: Record<string, string> = {}
    const wallGuidToElementType: Record<string, ElementTypes> = {}
    const guidToAdditionalTranslation: Record<string, Vector3Tuple> = {}
    const hidden: Record<string, boolean> = {}

    walls.forEach(wall => {
      const assembly = elementIdToAssembly[wall.guid]
      // The load-bearing layer only, like the plan PDF's wall footprints
      // (`assembly.structuralThickness`), not the full assembly build-up.
      const thickness =
        (assembly && calculateStructuralAssemblyThickness(assembly)) || FALLBACK_WALL_THICKNESS
      const [start, end] = wall.shape.points
      if (!start || !end) return

      const isWallHidden = guidToHidden?.[wall.guid] ?? false
      const elementType: ElementTypes =
        wall.placement === 'External' ? 'outer_walls' : 'inner_walls'
      const zTranslation = storeyToElementTypeAndZTranslation?.[wall.storey]?.[elementType] ?? 0

      elements.push({
        id: wall.guid,
        start: [start.x, start.y, start.z],
        end: [end.x, end.y, end.z],
        width: thickness,
        height: WALL_BODY_HEIGHT,
      })
      hidden[wall.guid] = isWallHidden
      wallGuidToElementType[wall.guid] = elementType
      if (zTranslation) guidToAdditionalTranslation[wall.guid] = [0, 0, zTranslation]

      // The stiffening plan draws the full wall footprint without its openings.
      if (hideOpenings) return

      wall.openings?.forEach(opening => {
        const [openingStart, openingEnd] = opening.shape?.points ?? []
        if (!openingStart || !openingEnd) return

        elements.push({
          id: opening.guid,
          start: [openingStart.x, openingStart.y, start.z],
          end: [openingEnd.x, openingEnd.y, start.z],
          width: thickness,
          height: WALL_BODY_HEIGHT,
        })
        openingGuidToWallGuid[opening.guid] = wall.guid
        hidden[opening.guid] = isWallHidden
        guidToAdditionalTranslation[opening.guid] = [0, 0, zTranslation + OPENING_Z_LIFT]
      })
    })

    return {
      elements,
      openingGuidToWallGuid,
      wallGuidToElementType,
      guidToAdditionalTranslation,
      guidToHidden: hidden,
    }
  }, [walls, elementIdToAssembly, guidToHidden, storeyToElementTypeAndZTranslation, hideOpenings])

  const guidToColor = useMemo(() => {
    const colors: Record<string, string> = {}

    walls.forEach(wall => {
      if (selectedElements?.includes(wall.guid)) {
        colors[wall.guid] = scenePalette.selection as string
      } else if (planColors) {
        colors[wall.guid] = planColors.fill
      } else {
        colors[wall.guid] =
          wall.placement === 'External'
            ? (scenePalette.elements3d.externalWalls as string)
            : (scenePalette.elements3d.internalWalls as string)
      }
    })
    Object.keys(openingGuidToWallGuid).forEach(openingGuid => {
      colors[openingGuid] = planColors?.openingFill ?? OPENING_COLOR
    })

    return colors
  }, [walls, openingGuidToWallGuid, selectedElements, scenePalette, planColors])

  const guidToOutlinesColor = useMemo(() => {
    return elements.reduce((acc, element) => {
      const planOutline = openingGuidToWallGuid[element.id]
        ? planColors?.openingOutline
        : planColors?.outline
      acc[element.id] = planOutline ?? (scenePalette.outlinesDark as string)
      return acc
    }, {} as Record<string, string>)
  }, [elements, openingGuidToWallGuid, scenePalette, planColors])

  const guidToOpacity = useMemo(() => {
    return elements.reduce((acc, element) => {
      acc[element.id] = 'notTransparent' as OpacityOption
      return acc
    }, {} as Record<string, OpacityOption>)
  }, [elements])

  const guidToRenderMode = useMemo(() => {
    return elements.reduce((acc, element) => {
      acc[element.id] = { bodyGeometry: true, outline: true }
      return acc
    }, {} as Record<string, LineShapesRenderMode>)
  }, [elements])

  // Clicks on an opening footprint should behave like clicks on its wall. Walls whose type
  // is not selectable are ignored, mirroring the FloorplanMesh which disables raycasting for them.
  const resolveSelectableWallGuid = useCallback(
    (guid: string): string | undefined => {
      const wallGuid = openingGuidToWallGuid[guid] ?? guid
      const elementType = wallGuidToElementType[wallGuid]
      if (elementType && typeToSelectable?.[elementType] === false) return undefined
      return wallGuid
    },
    [openingGuidToWallGuid, wallGuidToElementType, typeToSelectable],
  )

  const handleClick = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      const wallGuid = resolveSelectableWallGuid(guid)
      if (wallGuid) onClickElement?.(wallGuid, event)
    },
    [onClickElement, resolveSelectableWallGuid],
  )

  const handleContextMenu = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      const wallGuid = resolveSelectableWallGuid(guid)
      if (wallGuid) onContextMenuElement?.(wallGuid, event)
    },
    [onContextMenuElement, resolveSelectableWallGuid],
  )

  if (elements.length === 0) return null

  return (
    <LineShapesMesh
      elements={elements}
      guidToColor={guidToColor}
      guidToOutlinesColor={guidToOutlinesColor}
      guidToHidden={guidToHiddenMerged}
      guidToOpacity={guidToOpacity}
      guidToRenderMode={guidToRenderMode}
      guidToAdditionalTranslation={guidToAdditionalTranslation}
      opacityOptionValues={OPACITY_OPTION_VALUES}
      zOffset={Z_OFFSET}
      handleClick={handleClick}
      handleContextMenu={handleContextMenu}
    />
  )
}

export default RealThicknessWallsMesh
